import React from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Container, Row, Col } from "reactstrap";
import { CognitoStore } from "src/stores/cognito-store";
import { StripeVerification, StripeVerificationCb } from "./StripeVerification";
import { PsModal } from "./Modal";

export interface StripeVerificationProps {
  firstName: string;
}
export const StripeVerificationForm: React.FunctionComponent<StripeVerificationProps> = (
  props: StripeVerificationProps,
) => {
  const [faqVis, setFaqVis] = React.useState(false);
  const stripePromise = loadStripe(process.env.STRIPE_KEY);
  const toggleFaqVis = (): void => {
    setFaqVis(!faqVis);
  };
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleCb = (data: StripeVerificationCb): void => {
    if (data.success) {
      // refresh session
      CognitoStore.refreshSession()
        .then(() => {
          return window.location.reload();
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
  return (
    <div className="verification-page">
      <Container>
        <div className="content">
          <Row>
            <Col>
              <h4 className="fw500 cta">Hello {props.firstName}, ready to get started?</h4>
              <div className="mt-3 text-start">
                <p>
                  Psychedelic Support verifies the identity of users who submit profiles using your Name, DOB and last 4
                  of SSN. We do this to protect all users of the platform.
                </p>
                <p>If you prefer, please email us for manual verification.</p>
              </div>
            </Col>
          </Row>
          <Row className="mt-4 verify">
            <Col>
              <Elements stripe={stripePromise}>
                <StripeVerification cb={handleCb} />
              </Elements>
            </Col>
          </Row>
        </div>
        <div className="faq">
          <Row className="mt-4">
            <Col>
              <div className="font-small">
                Questions?
                <br />
                <a className="cursor-pointer" onClick={toggleFaqVis}>
                  Learn more about Identity Verification
                </a>
              </div>
              <PsModal visible={faqVis} toggleCb={toggleFaqVis} title={"FAQ Psychedelic Support Identity"}>
                <h5 className="cta fw500">How does identity verification work?</h5>
                <p>
                  Psychedelic Support works with{" "}
                  <a href="https://stripe.com/" target="_blank" rel="noreferrer">
                    Stripe
                  </a>{" "}
                  to conduct identity verification online. Stripe builds technology that’s used by millions of companies
                  around the world. They help with everything from accepting payments to managing subscriptions to
                  verifying identities.
                  <p>Stripe helps Psychedelic Support confirm your identity by conducting the following checks:</p>
                  <ol className="mt-2">
                    <li>
                      Stripe will collect your name, date of birth, and last four of government ID number, and validate
                      that it is real. They will check this information against a global set of databases to confirm
                      that it exists.
                    </li>
                  </ol>
                  Stripe will ask for your consent before collecting and using your information. They will only use your
                  verification data in accordance with the{" "}
                  <a
                    href="https://support.stripe.com/questions/common-questions-about-stripe-identity#verification-permissions"
                    target="_blank"
                    rel="noreferrer"
                  >
                    permissions
                  </a>{" "}
                  you grant before starting the verification process, and based on their{" "}
                  <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  .
                </p>
                <h5 className="cta fw500">Best practices for a successful verification:</h5>
                <p>
                  Before starting the verification process, here’s what you need:
                  <ol className="mt-2">
                    <li>
                      Your name, birthdate and last four of your government issued Id (currently only available for US
                      residents).
                    </li>
                  </ol>
                </p>
                <h5 className="cta fw500">Why am I asked to verify my identity?</h5>
                <p>
                  Psychedelic Support verifies the identity of users who post profiles on our site in order to protect
                  the users of Psychedelic Support. You can opt out of the identity verification process by{" "}
                  <a target="_blank" href="https://calendly.com/psysupp" rel="noreferrer">
                    scheduling an onboarding call with Psychedelic Support
                  </a>
                  .
                </p>
                <h5 className="cta fw500">Who has access to my verification data?</h5>
                <p>
                  Both Psychedelic Support and Stripe will have access to the information that you submit through the
                  verification flow. We rely on Stripe to help store your verification data. Stripe uses access controls
                  and security standards that are at least as stringent as those used to handle their own KYC and
                  payments compliance data.
                </p>
                <h5 className="cta fw500">How can I access or delete my verification data? </h5>
                <p>
                  After your verification is complete, Psychedelic Support sends a request to Stripe to redact all
                  information gathered during the verification. Psychedelic Support does not persist any personally
                  identifiable information gathered during the verification flow.
                </p>
              </PsModal>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};
